import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import PageHeader from '../components/PageHeader';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import timesheetService, { type TimesheetHistory, type TimesheetEntry } from '../services/timesheet';
import { useAuth } from '../context/AuthContext';
import {
  useOrgTimezone,
  formatTimeInTz,
  formatDateInTz,
  formatLocalDateInTz,
  formatDateTimeForInput,
  dateTimeLocalToISO,
} from '../utils/timezone';
import { formatDuration } from '../utils/time';

interface DateRange {
  start: string;
  end: string;
}

/** Shift a YYYY-MM-DD date by n days using UTC arithmetic (no local-midnight drift). */
function addDays(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.substring(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().substring(0, 10);
}

/** Inclusive day count between two YYYY-MM-DD dates. */
function daysBetween(start: string, end: string): number {
  const [sy, sm, sd] = start.substring(0, 10).split('-').map(Number);
  const [ey, em, ed] = end.substring(0, 10).split('-').map(Number);
  return Math.round((Date.UTC(ey, em - 1, ed) - Date.UTC(sy, sm - 1, sd)) / 86_400_000) + 1;
}

const TimesheetHistoryPage: React.FC = () => {
  const { user } = useAuth();
  const orgTimezone = useOrgTimezone();
  const isFortnightly = user?.pay_frequency === 'fortnightly';

  const [history, setHistory] = useState<TimesheetHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Pay-cycle scoping. The current cycle's boundaries come from the backend
  // summary (authoritative for the org's pay frequency / start day); older
  // cycles are derived by stepping back one whole cycle at a time.
  const [currentCycle, setCurrentCycle] = useState<DateRange | null>(null);
  const [cycleLoading, setCycleLoading] = useState(true);
  const [cycleOffset, setCycleOffset] = useState(0); // 0 = current, -1 = previous, …

  // Optional manual override of the pay-cycle window.
  const [customRange, setCustomRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appliedRange, setAppliedRange] = useState<DateRange | null>(null);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimesheetEntry | null>(null);
  const [editClockIn, setEditClockIn] = useState('');
  const [editClockOut, setEditClockOut] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editSkipLunch, setEditSkipLunch] = useState(false);
  const [editReason, setEditReason] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Resolve the current pay cycle once (and again if pay frequency loads late).
  useEffect(() => {
    let cancelled = false;

    const loadCycle = async () => {
      setCycleLoading(true);
      try {
        const res = await timesheetService.getSummary({ period: isFortnightly ? 'fortnight' : 'week' });
        const s = res.success ? res.data.summary : null;
        if (!cancelled && s?.period_start && s?.period_end) {
          setCurrentCycle({ start: s.period_start.substring(0, 10), end: s.period_end.substring(0, 10) });
        }
      } catch {
        // If the cycle can't be resolved we fall back to an unscoped list.
      } finally {
        if (!cancelled) setCycleLoading(false);
      }
    };

    loadCycle();
    return () => { cancelled = true; };
  }, [isFortnightly]);

  const cycleLength = currentCycle ? daysBetween(currentCycle.start, currentCycle.end) : 0;

  const activeRange = useMemo<DateRange | null>(() => {
    if (customRange) return appliedRange;
    if (!currentCycle) return null;
    const shift = cycleOffset * cycleLength;
    return { start: addDays(currentCycle.start, shift), end: addDays(currentCycle.end, shift) };
  }, [customRange, appliedRange, currentCycle, cycleLength, cycleOffset]);

  const fetchHistory = useCallback(async () => {
    // Hold off the first fetch until the pay cycle is known, so the default
    // view is already scoped to the current cycle rather than everything.
    if (!customRange && cycleLoading) return;

    setLoading(true);
    setError(null);

    try {
      const params: { page: number; limit: number; startDate?: string; endDate?: string } = {
        page: currentPage,
        limit: 50,
      };
      if (activeRange?.start) params.startDate = activeRange.start;
      if (activeRange?.end) params.endDate = activeRange.end;

      const response = await timesheetService.getHistory(params);
      if (response.success) {
        setHistory(response.data);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch timesheet history');
    } finally {
      setLoading(false);
    }
  }, [currentPage, activeRange, customRange, cycleLoading]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const goToPreviousCycle = () => {
    setCycleOffset(o => o - 1);
    setCurrentPage(1);
  };

  const goToNextCycle = () => {
    setCycleOffset(o => Math.min(0, o + 1));
    setCurrentPage(1);
  };

  const handleDateFilter = () => {
    if (!startDate && !endDate) return;
    setAppliedRange({ start: startDate, end: endDate });
    setCurrentPage(1);
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setAppliedRange(null);
    setCustomRange(false);
    setCycleOffset(0);
    setCurrentPage(1);
  };

  const rangeLabel =
    activeRange?.start && activeRange?.end
      ? `${formatLocalDateInTz(activeRange.start, orgTimezone, { day: 'numeric', month: 'short' })} – ${formatLocalDateInTz(activeRange.end, orgTimezone, { day: 'numeric', month: 'short', year: 'numeric' })}`
      : 'All records';

  const formatTime = (dateString: string) => {
    return formatTimeInTz(dateString, orgTimezone);
  };

  // Include the weekday so a glance at the row tells you which day it was.
  const formatDate = (dateString: string) => {
    return formatDateInTz(dateString, orgTimezone, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };


  const handleEditClick = (entry: TimesheetEntry) => {
    setEditingEntry(entry);
    setEditClockIn(formatDateTimeForInput(entry.clock_in_time, orgTimezone));
    setEditClockOut(entry.clock_out_time ? formatDateTimeForInput(entry.clock_out_time, orgTimezone) : '');
    setEditNotes(entry.notes || '');
    setEditSkipLunch(entry.skip_lunch_deduction || false);
    setEditReason('');
    setEditError(null);
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editingEntry) return;

    if (!editReason.trim()) {
      setEditError('Please provide a reason for editing this timesheet');
      return;
    }

    // Both inputs are datetime-local in org time, so the date is the first ten
    // characters. The server refuses an entry spanning two days; say so here, where
    // the second picker left on the wrong day can still be fixed.
    if (editClockOut && editClockIn && editClockOut.slice(0, 10) !== editClockIn.slice(0, 10)) {
      setEditError('Clock in and clock out must be on the same day — check the date on the clock out time.');
      return;
    }

    setEditLoading(true);
    setEditError(null);

    try {
      // Convert the datetime-local values (in org timezone) back to UTC ISO strings
      const updateData: any = {
        clock_in_time: dateTimeLocalToISO(editClockIn, orgTimezone),
        edit_reason: editReason
      };

      if (editClockOut) {
        updateData.clock_out_time = dateTimeLocalToISO(editClockOut, orgTimezone);
        // Carry the lunch choice so the backend doesn't re-apply a default deduction
        updateData.skip_lunch_deduction = editSkipLunch;
      }

      if (editNotes) {
        updateData.notes = editNotes;
      }

      const response = await timesheetService.updateTimesheet(editingEntry.id, updateData);
      
      if (response.success) {
        setEditModalOpen(false);
        setEditingEntry(null);
        fetchHistory(); // Refresh the list
      }
    } catch (error: any) {
      // The api interceptor rejects with an ApiError carrying the server's
      // message; there is no `.response` on it.
      setEditError(error?.message || 'Failed to update timesheet');
    } finally {
      setEditLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditModalOpen(false);
    setEditingEntry(null);
    setEditError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Timesheet History"
        subtitle="View your work time records"
        icon={<Clock className="h-6 w-6 text-white" />}
      />

      <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">

        {/* Pay-cycle navigator */}
        <Card className="p-4 sm:p-5 mb-6">
          <div className="flex items-center justify-between gap-2">
            <Button
              onClick={goToPreviousCycle}
              variant="secondary"
              size="sm"
              disabled={customRange || !currentCycle}
              aria-label="Previous pay cycle"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline ml-1">Previous</span>
            </Button>

            <div className="min-w-0 text-center">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                {customRange ? 'Custom range' : cycleOffset === 0 ? 'Current pay cycle' : `${Math.abs(cycleOffset)} cycle${Math.abs(cycleOffset) === 1 ? '' : 's'} ago`}
              </div>
              <div className="text-sm font-semibold text-gray-900 truncate">
                {cycleLoading && !customRange ? 'Loading…' : rangeLabel}
              </div>
            </div>

            <Button
              onClick={goToNextCycle}
              variant="secondary"
              size="sm"
              disabled={customRange || !currentCycle || cycleOffset >= 0}
              aria-label="Next pay cycle"
            >
              <span className="hidden sm:inline mr-1">Next</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-center gap-3 text-sm">
            {!customRange && cycleOffset !== 0 && (
              <button
                onClick={handleClearFilter}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Back to current cycle
              </button>
            )}
            <button
              onClick={() => (customRange ? handleClearFilter() : setCustomRange(true))}
              className="text-gray-500 hover:text-gray-700"
            >
              {customRange ? 'Use pay cycles' : 'Custom date range'}
            </button>
          </div>

          {customRange && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 border-t border-gray-200 pt-4">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
                  className="w-full"
                />
              </div>

              <div className="flex items-end">
                <Button onClick={handleDateFilter} className="w-full">
                  Apply Filter
                </Button>
              </div>

              <div className="flex items-end">
                <Button onClick={handleClearFilter} variant="secondary" className="w-full">
                  Clear Filter
                </Button>
              </div>
            </div>
          )}
        </Card>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {loading ? (
          <Card className="p-8 text-center">
            <div className="text-gray-500">Loading timesheet history...</div>
          </Card>
        ) : history && history.entries.length > 0 ? (
          <>
            {/* Mobile card list */}
            <div className="md:hidden space-y-3">
              {history.entries.map((entry: TimesheetEntry) => (
                <Card key={entry.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-sm font-semibold text-gray-900">
                      {formatDate(entry.clock_in_time)}
                    </div>
                    <div className="flex flex-wrap justify-end gap-1">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        entry.status === 'clocked_in'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {entry.status === 'clocked_in' ? 'Active' : 'Completed'}
                      </span>
                      {entry.edited && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          Edited
                        </span>
                      )}
                      {entry.missed_clock && (
                        <span
                          className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800"
                          title={entry.missed_clock_reason || 'Scheduled hours claimed — no clock in/out recorded'}
                        >
                          No clock record
                        </span>
                      )}
                      {entry.payroll_category && entry.payroll_category !== 'Base Hourly' && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                          {entry.payroll_category}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-wide text-gray-500">In</div>
                      <div className="text-gray-900">{formatTime(entry.clock_in_time)}</div>
                      {entry.edited && entry.original_clock_in_time &&
                        new Date(entry.original_clock_in_time).getTime() !== new Date(entry.clock_in_time).getTime() && (
                        <div className="text-xs text-gray-400 line-through">
                          {formatTime(entry.original_clock_in_time)}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-wide text-gray-500">Out</div>
                      <div className="text-gray-900">
                        {entry.clock_out_time ? formatTime(entry.clock_out_time) : '-'}
                      </div>
                      {entry.edited && entry.original_clock_out_time && entry.clock_out_time &&
                        new Date(entry.original_clock_out_time).getTime() !== new Date(entry.clock_out_time).getTime() && (
                        <div className="text-xs text-gray-400 line-through">
                          {formatTime(entry.original_clock_out_time)}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-wide text-gray-500">Total</div>
                      <div className="text-gray-900">
                        {entry.total_hours ? formatDuration(entry.total_hours) : '-'}
                      </div>
                    </div>
                  </div>

                  {(entry.notes || (entry.edited && entry.edit_reason)) && (
                    <div className="mt-3 text-sm text-gray-600">
                      {entry.notes && <p className="break-words">{entry.notes}</p>}
                      {entry.edited && entry.edit_reason && (
                        <p className="text-xs text-blue-600 mt-1 break-words">
                          Edit reason: {entry.edit_reason}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="mt-3">
                    <Button
                      onClick={() => handleEditClick(entry)}
                      size="sm"
                      variant="secondary"
                      className="w-full"
                    >
                      Edit
                    </Button>
                  </div>
                </Card>
              ))}
            </div>

            {/* History Table */}
            <Card className="hidden md:block overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Clock In
                      </th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Clock Out
                      </th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Hours
                      </th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Notes
                      </th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {history.entries.map((entry: TimesheetEntry) => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatDate(entry.clock_in_time)}
                        </td>
                        <td className="px-4 lg:px-6 py-4 text-sm text-gray-500">
                          <div className="flex flex-col">
                            <span>{formatTime(entry.clock_in_time)}</span>
                            {entry.edited && entry.original_clock_in_time &&
                              new Date(entry.original_clock_in_time).getTime() !== new Date(entry.clock_in_time).getTime() && (
                              <span className="text-xs text-gray-400 line-through">
                                Was: {formatTime(entry.original_clock_in_time)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 text-sm text-gray-500">
                          <div className="flex flex-col">
                            <span>{entry.clock_out_time ? formatTime(entry.clock_out_time) : '-'}</span>
                            {entry.edited && entry.original_clock_out_time && entry.clock_out_time &&
                              new Date(entry.original_clock_out_time).getTime() !== new Date(entry.clock_out_time).getTime() && (
                              <span className="text-xs text-gray-400 line-through">
                                Was: {formatTime(entry.original_clock_out_time)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {entry.total_hours ? formatDuration(entry.total_hours) : '-'}
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full inline-block ${
                              entry.status === 'clocked_in'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {entry.status === 'clocked_in' ? 'Active' : 'Completed'}
                            </span>
                            {entry.edited && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 inline-block">
                                Edited
                              </span>
                            )}
                            {entry.missed_clock && (
                              <span
                                className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800 inline-block"
                                title={entry.missed_clock_reason || 'Scheduled hours claimed — no clock in/out recorded'}
                              >
                                No clock record
                              </span>
                            )}
                            {entry.payroll_category && entry.payroll_category !== 'Base Hourly' && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 inline-block">
                                {entry.payroll_category}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 text-sm text-gray-500">
                          <div className="max-w-xs">
                            {entry.notes && (
                              <p className="truncate" title={entry.notes}>{entry.notes}</p>
                            )}
                            {entry.edited && entry.edit_reason && (
                              <p className="text-xs text-blue-600 mt-1 truncate" title={entry.edit_reason}>
                                Edit reason: {entry.edit_reason}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm">
                          <Button
                            onClick={() => handleEditClick(entry)}
                            size="sm"
                            variant="secondary"
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Pagination */}
            {history.pagination && history.pagination.totalPages > 1 && (
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-sm text-gray-700 text-center sm:text-left">
                  Showing page {history.pagination.page} of {history.pagination.totalPages} 
                  ({history.pagination.total} total entries)
                </div>
                
                <div className="flex space-x-2">
                  <Button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    variant="secondary"
                    size="sm"
                  >
                    Previous
                  </Button>
                  
                  <Button
                    onClick={() => setCurrentPage(Math.min(history.pagination.totalPages, currentPage + 1))}
                    disabled={currentPage === history.pagination.totalPages}
                    variant="secondary"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <Card className="p-8 text-center">
            <div className="text-gray-500">
              {customRange
                ? 'No timesheet entries found for the selected date range.'
                : currentCycle
                  ? `No timesheet entries for this pay cycle (${rangeLabel}).`
                  : 'No timesheet entries found.'}
            </div>
          </Card>
        )}

        {/* Edit Modal */}
        {editModalOpen && editingEntry && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold">Edit Timesheet</h2>
                  <button
                    onClick={handleCancelEdit}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {editError && (
                  <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {editError}
                  </div>
                )}

                {editingEntry.edited && (
                  <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
                    <p className="font-semibold">This entry has been edited before</p>
                    {editingEntry.original_clock_in_time &&
                      new Date(editingEntry.original_clock_in_time).getTime() !== new Date(editingEntry.clock_in_time).getTime() && (
                      <p className="text-sm mt-1">
                        Original Clock In: {formatTime(editingEntry.original_clock_in_time)}
                      </p>
                    )}
                    {editingEntry.original_clock_out_time && editingEntry.clock_out_time &&
                      new Date(editingEntry.original_clock_out_time).getTime() !== new Date(editingEntry.clock_out_time).getTime() && (
                      <p className="text-sm">
                        Original Clock Out: {formatTime(editingEntry.original_clock_out_time)}
                      </p>
                    )}
                    {editingEntry.edit_reason && (
                      <p className="text-sm mt-1">
                        Previous edit reason: {editingEntry.edit_reason}
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label htmlFor="editClockIn" className="block text-sm font-medium text-gray-700 mb-2">
                      Clock In Time *
                    </label>
                    <Input
                      id="editClockIn"
                      type="datetime-local"
                      value={editClockIn}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditClockIn(e.target.value)}
                      className="w-full"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="editClockOut" className="block text-sm font-medium text-gray-700 mb-2">
                      Clock Out Time
                    </label>
                    <Input
                      id="editClockOut"
                      type="datetime-local"
                      value={editClockOut}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditClockOut(e.target.value)}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="editNotes" className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      id="editNotes"
                      value={editNotes}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditNotes(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                    />
                  </div>

                  {editClockOut && (
                    <div>
                      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={editSkipLunch}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditSkipLunch(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        No lunch break taken
                      </label>
                    </div>
                  )}

                  <div>
                    <label htmlFor="editReason" className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for Edit *
                    </label>
                    <textarea
                      id="editReason"
                      value={editReason}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                      placeholder="Explain why you're editing this timesheet..."
                      required
                    />
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-6">
                    <Button
                      onClick={handleCancelEdit}
                      variant="secondary"
                      disabled={editLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleEditSubmit}
                      disabled={editLoading}
                    >
                      {editLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimesheetHistoryPage;
